import React from 'react';
import { Avatar, Box, Menu, MenuItem, ListItemIcon, Typography } from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  DarkModeOutlined,
  LightModeOutlined,
  LogoutOutlined,
  PersonOutlineOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { portaledMenuSlotProps, portaledMenuSx } from '../utils/portaledPopperProps';
import { CHROME_OVERLAY_Z, getNavTokens } from './navTokens';
import { LogoutConfirmationDialog } from '../components/LogoutConfirmationDialog';

const USER_LEVEL_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Administrador',
  VIEWER: 'Visualizador',
  MANAGER: 'Gestor',
  USER: 'Usuário',
};

export const formatUserLevel = (level: string) => USER_LEVEL_LABELS[level] ?? level;

export const getInitials = (name?: string) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

interface AccountMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  name?: string;
  email?: string;
  companyName?: string;
  userLevel?: string;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

/** Único lugar de identidade e sessão: perfil, preferências, tema e sair. */
export const AccountMenu: React.FC<AccountMenuProps> = ({
  anchorEl,
  onClose,
  name,
  email,
  companyName,
  userLevel,
  isDarkMode,
  onToggleTheme,
  onNavigate,
  onLogout,
}) => {
  const t = getNavTokens(useMuiTheme());
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = React.useState(false);
  const go = (path: string) => {
    onClose();
    onNavigate(path);
  };
  const role = userLevel ? formatUserLevel(userLevel) : undefined;
  const itemSx = { minHeight: 40, borderRadius: '10px', fontSize: '0.84rem', gap: 0.5 };
  const iconSx = { minWidth: 32, color: t.muted };

  // Indicador do tema atual; o clique em qualquer ponto da linha alterna o tema.
  const themeOption = (dark: boolean, label: string, Icon: typeof LightModeOutlined) => {
    const selected = isDarkMode === dark;
    return (
      <Box
        component="span"
        title={label}
        sx={{
          width: 40,
          height: 30,
          borderRadius: '9px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: selected ? t.ink : t.muted,
          backgroundColor: selected ? t.chrome : 'transparent',
          boxShadow: selected ? '0 1px 2px rgba(15,23,42,0.14)' : 'none',
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
    );
  };

  return (
    <>
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        ...portaledMenuSlotProps,
        paper: {
          sx: {
            mt: 0.5,
            width: 300,
            borderRadius: '16px',
            backgroundImage: 'none',
            boxShadow: '0 12px 32px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
          },
        },
        list: { sx: { p: 1 } },
      }}
      sx={{ ...portaledMenuSx, zIndex: CHROME_OVERLAY_Z }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, pt: 1, pb: 1.5 }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#3A6CA8', fontSize: 15, fontWeight: 600 }}>
          {getInitials(name || email)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{name || email}</Typography>
          {name && email && (
            <Typography noWrap sx={{ fontSize: '0.78rem', color: t.muted }}>{email}</Typography>
          )}
          {(companyName || role) && (
            <Typography noWrap sx={{ fontSize: '0.78rem', color: t.muted }}>
              {[companyName, role].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
      </Box>
      <MenuItem onClick={() => go('/profile')} sx={itemSx}>
        <ListItemIcon sx={iconSx}><PersonOutlineOutlined fontSize="small" /></ListItemIcon>
        Perfil e segurança
      </MenuItem>
      <MenuItem onClick={() => go('/profile?secao=preferencias')} sx={itemSx}>
        <ListItemIcon sx={iconSx}><TuneOutlined fontSize="small" /></ListItemIcon>
        Preferências
      </MenuItem>
      <MenuItem
        onClick={onToggleTheme}
        aria-label={`Tema: ${isDarkMode ? 'escuro' : 'claro'}. Alternar para ${isDarkMode ? 'claro' : 'escuro'}`}
        sx={{ ...itemSx, minHeight: 48, pr: 0.75 }}
      >
        <Box component="span" sx={{ flex: 1 }}>Tema</Box>
        <Box component="span" aria-hidden="true" sx={{ display: 'flex', gap: '2px', p: '3px', borderRadius: '12px', bgcolor: t.canvas }}>
          {themeOption(false, 'Claro', LightModeOutlined)}
          {themeOption(true, 'Escuro', DarkModeOutlined)}
        </Box>
      </MenuItem>
      <MenuItem onClick={() => { onClose(); setLogoutConfirmationOpen(true); }} sx={{ ...itemSx, mt: 0.75 }}>
        <ListItemIcon sx={iconSx}><LogoutOutlined fontSize="small" /></ListItemIcon>
        Sair
      </MenuItem>
    </Menu>
    <LogoutConfirmationDialog open={logoutConfirmationOpen} onClose={() => setLogoutConfirmationOpen(false)} onConfirm={onLogout} />
    </>
  );
};
